import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';
import { useApplicationStore } from '../store/application.store';
import { useNavigationStore } from '../store/navigation.store';
import { useValidationStore } from '../store/validation.store';
import { useSignerStore } from '../store/signer.store';
import { formSubmissionService } from '../services/form-submission.service';
import { withRecaptchaTimeout } from '../utils/timeout.utils';

import { FormPage } from '../types/forms';

// REMOVED local type declaration - now handled by src/types/global.d.ts

interface UseFormSubmissionProps {
  mappingShortId: string | undefined;
}

// Purpose: Custom hook to handle form submission logic
export const useFormSubmission = ({ mappingShortId }: UseFormSubmissionProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { submitForm } = useApplicationStore();
  const { getCurrentPage, setActivePage } = useNavigationStore();
  const { validatePage, validateAllPages } = useValidationStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Purpose: Scrolls to top of page with smooth behavior
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Purpose: Handles navigation between pages with scroll behavior
  const handleNavigation = (direction: 'next' | 'back') => {
    const { handleNextPage, handlePreviousPage, activePage } = useNavigationStore.getState();

    // Store the current page before navigation
    const currentPage = activePage;

    if (direction === 'next') {
      handleNextPage();
    } else {
      handlePreviousPage();
    }

    // Use setTimeout to ensure state has updated before scrolling
    setTimeout(() => {
      const newPage = useNavigationStore.getState().activePage;
      // Only scroll if the page actually changed
      if (newPage !== currentPage) {
        scrollToTop();
      }
    }, 0);
  };

  // Purpose: Find an email from the form data for submission
  const findEmailInFormData = () => {
    const { structure } = useApplicationStore.getState();
    if (!structure?.pages) return null;

    // Look for email fields in the form data
    for (const page of structure.pages) {
      for (const section of page.sections) {
        for (const field of section.fields) {
          // Check if it's an email field and has a value
          if (field.type === 'email' && field.value && typeof field.value === 'string') {
            if (field.value.trim() !== '' && field.value.includes('@')) {
              return field.value.trim();
            }
          }
        }
      }
    }
    return null;
  };

  // Purpose: Handles the form submission process
  const handleSubmit = async () => {
    try {
      if (isSubmitting) {
        logger.warn('SubmissionFlow', 'Submission already in progress, ignoring duplicate request');
        return;
      }

      // Check if reCAPTCHA script is loaded
      const grecaptchaAny = window.grecaptcha as any;
      if (!grecaptchaAny || !grecaptchaAny.enterprise) {
        logger.error('SubmissionFlow', 'reCAPTCHA Enterprise script not loaded');
        toast.error('Security verification component not loaded. Please refresh the page.');
        return;
      }

      setIsSubmitting(true);

      let recaptchaToken: string | null = null;
      const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

      if (!siteKey) {
        logger.error('SubmissionFlow', 'Missing VITE_RECAPTCHA_SITE_KEY environment variable');
        toast.error('Security configuration error. Please contact support.');
        setIsSubmitting(false);
        return;
      }

      try {
        // Add timeout wrapper for reCAPTCHA execution using utility
        recaptchaToken = await withRecaptchaTimeout(
          new Promise<string>((resolve, reject) => {
            (window.grecaptcha as any).enterprise.ready(async () => {
              try {
                const token = await (window.grecaptcha as any).enterprise.execute(siteKey, {
                  action: 'submit_application',
                });
                resolve(token);
              } catch (err) {
                reject(err);
              }
            });
          }),
          'execution'
        );

        if (!recaptchaToken) {
          throw new Error('reCAPTCHA token generation failed');
        }
      } catch (error) {
        logger.error('SubmissionFlow', 'reCAPTCHA Enterprise execution failed', {
          error,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        
        // Provide better error message for timeout
        const isTimeout = error instanceof Error && error.message.includes('timeout');
        toast.error(
          isTimeout 
            ? 'Security verification timed out. Please check your internet connection and try again.'
            : 'Security verification failed. Please try again.'
        );
        setIsSubmitting(false);
        return;
      }

      const { structure, recordId } = useApplicationStore.getState();
      const { activePage } = useNavigationStore.getState();
      const { invalidFieldsPageMap } = useValidationStore.getState();

      // Find any email in the form data for submission
      const submissionEmail = findEmailInFormData();

      // Validate all fields before submission
      logger.info('SubmissionFlow', 'Starting form validation before submission', {
        recordId,
        pageCount: structure?.pages?.length || 0,
        activePage,
        hasSubmissionEmail: !!submissionEmail,
      });

      // Check if terms consent has been given
      const { termsConsent, contactConsent, setContactConsent } = useApplicationStore.getState();
      const hasProgressedPastFirstPage = activePage > 0;

      // For resumed in-progress applications, contact consent may not survive hydration in-memory.
      // If the applicant is already past page 1, recover consent to avoid false submit blocks.
      if (!contactConsent && hasProgressedPastFirstPage) {
        setContactConsent(true);
        logger.warn(
          'SubmissionFlow',
          'Recovered contact consent for resumed application before submit',
          {
            activePage,
            recordId,
          }
        );
      } else if (!contactConsent) {
        logger.warn('SubmissionFlow', 'Contact consent required but not given');
        toast.error('You must agree to contact consent before submitting');
        setIsSubmitting(false);
        return;
      }

      if (!termsConsent) {
        logger.warn('SubmissionFlow', 'Terms consent required but not given');
        toast.error('You must agree to the terms and conditions before submitting');
        setIsSubmitting(false);
        return;
      }

      // Get current page for validation
      const currentPage = getCurrentPage();
      if (!currentPage) {
        logger.error('SubmissionFlow', 'Cannot submit - current page not found');
        toast.error('An error occurred. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // First validate the current page
      if (!structure) {
        toast.error('Form structure not available');
        setIsSubmitting(false);
        return;
      }
      const isCurrentPageValid = await validatePage(
        currentPage.id,
        structure,
        useApplicationStore.getState().viewableFields,
        useApplicationStore.getState().editableFields,
        useApplicationStore.getState().files
      );
      if (!isCurrentPageValid) {
        formSubmissionService.logValidationFailure(currentPage);
        toast.error('Please fill in all required fields on this page before submitting.');
        setIsSubmitting(false);
        return;
      }

      // Then validate all pages
      const { isValid, firstInvalidPageIndex, invalidFieldsByType } = await validateAllPages(
        structure,
        useApplicationStore.getState().viewableFields,
        useApplicationStore.getState().editableFields,
        useApplicationStore.getState().files
      );

      if (!isValid) {
        // Create a properly typed validation failure object
        const validationFailurePage: FormPage = {
          id: 'all-pages',
          title: 'Form Validation',
          sections: [],
        };

        formSubmissionService.logValidationFailure(validationFailurePage);

        // Navigate to the first page with errors and scroll to top
        if (firstInvalidPageIndex !== null) {
          setActivePage(firstInvalidPageIndex);
          scrollToTop();

          // Show appropriate error message based on validation failures
          if (invalidFieldsByType.required.length > 0) {
            toast.error(
              `Please fill in all required fields before submitting (${invalidFieldsByType.required.length} empty fields).`
            );
          } else if (invalidFieldsByType.email.length > 0) {
            toast.error('Please enter valid email addresses in all email fields.');
          } else if (invalidFieldsByType.phone.length > 0) {
            toast.error('Please enter valid phone numbers (10+ digits) in all phone fields.');
          } else if (invalidFieldsByType.signature.length > 0) {
            toast.error('Please sign all required signature fields before submitting.');
          } else {
            // Check if we have other specific validation failures
            const invalidFieldIds = Object.keys(invalidFieldsPageMap);

            // Check for specific field types with formatting issues
            const hasFederalTaxIdError = invalidFieldIds.includes('federalTaxId');
            const hasZipCodeError = invalidFieldIds.includes('businessZip');

            if (hasFederalTaxIdError) {
              toast.error('Please enter a valid Federal Tax ID (XX-XXXXXXX).');
            } else if (hasZipCodeError) {
              toast.error('Please enter a valid ZIP code (XXXXX or XXXXX-XXXX).');
            } else {
              toast.error('Please correct the formatting errors on this page before submitting.');
            }
          }
        } else {
          toast.error('Please correct all errors before submitting.');
        }

        setIsSubmitting(false);
        return;
      }

      const { getUserId, userShortId, handleFieldValueChange } = useApplicationStore.getState();
      formSubmissionService.logSuccessfulValidation(getUserId());

      // Ensure we have an email for submission
      if (!submissionEmail) {
        toast.error(
          'Please enter your email address in at least one email field before submitting'
        );
        setIsSubmitting(false);
        return;
      }

      // Set application submitted timestamp before submitting
      try {
        const submittedAt = new Date().toISOString();
        handleFieldValueChange('applicationSubmittedTimestamp', submittedAt as unknown as string);
        logger.info('SubmissionFlow', 'Set applicationSubmittedTimestamp before submit', {
          applicationSubmittedTimestamp: submittedAt,
          userShortId,
        });
      } catch (error) {
        logger.warn('SubmissionFlow', 'Failed to set applicationSubmittedTimestamp (non-blocking)', {
          error,
          userShortId,
        });
      }

      logger.info('SubmissionFlow', 'Submitting form', {
        submissionEmail,
      });

      try {
        if (!mappingShortId) {
          logger.error('SubmissionFlow', 'Missing mappingShortId for submission');
          toast.error('A configuration error occurred. Please try again later.');
          setIsSubmitting(false);
          return;
        }

        // Submit the form
        const { getVisiblePages } = useNavigationStore.getState();
        const pagesForHeaders = getVisiblePages();
        const activeIdx = pagesForHeaders.findIndex((p) => p.id === currentPage.id);
        const result = await submitForm(recaptchaToken, mappingShortId, {
          pageCompletedIndex: activeIdx >= 0 ? activeIdx : undefined,
          pageCompletedId: currentPage.id,
          totalVisiblePages: pagesForHeaders.length,
        });
        const { recordId: _recordId } = useApplicationStore.getState(); // Get latest recordId (unused in new flow)

        // Check if submission was successful before navigating
        if (result.success) {
          logger.info('SubmissionFlow', 'Submission completed successfully', {
            result,
            submissionEmail,
          });

          // Get pdfOnly and document upload settings for navigation decisions
          const { pdfOnly, allowAdditionalSignerDocUpload, userShortId } = useApplicationStore.getState();
          const { currentSigner } = useSignerStore.getState();
          const shouldRedirectToCompletion =
            pdfOnly || (currentSigner > 1 && !allowAdditionalSignerDocUpload);

          if (shouldRedirectToCompletion) {
            if (!userShortId || !mappingShortId) {
              logger.warn(
                'SubmissionFlow',
                'Missing route params for submission completion redirect',
                {
                  userShortId,
                  mappingShortId,
                  pdfOnly,
                  currentSigner,
                  allowAdditionalSignerDocUpload,
                }
              );
              toast.success('Application submitted successfully.');
              return;
            }

            const completionPath = `/application/${userShortId}/${mappingShortId}/submission-complete${
              location.search || ''
            }`;

            logger.info('SubmissionFlow', 'Redirecting to submission completion page', {
              completionPath,
              pdfOnly,
              currentSigner,
              allowAdditionalSignerDocUpload,
            });

            navigate(completionPath, { replace: true });
            toast.success('Application submitted successfully.');
            return;
          }

          // For other modes, navigate to the Document Upload page after successful submission.
          const { getVisiblePages } = useNavigationStore.getState();
          const visiblePagesAfterSubmit = getVisiblePages();
          const documentUploadIndex = visiblePagesAfterSubmit.findIndex(
            (page) => page.id === 'document-upload'
          );

          if (documentUploadIndex >= 0) {
            logger.info(
              'SubmissionFlow',
              'Navigating to document-upload page after successful submission',
              {
                documentUploadIndex,
                totalPages: visiblePagesAfterSubmit.length,
                pdfOnly,
              }
            );

            setActivePage(documentUploadIndex);
            scrollToTop();
            toast.success('Application submitted successfully. You can now upload documents.');
          } else {
            logger.error(
              'SubmissionFlow',
              'Document-upload page not found after successful submission (staying on current page)',
              {
                totalPages: visiblePagesAfterSubmit.length,
                pageIds: visiblePagesAfterSubmit.map((p) => p.id),
                pdfOnly,
                currentSigner,
                allowAdditionalSignerDocUpload,
              }
            );
            toast.error(
              'Your application was submitted, but we could not open the document upload page. Please refresh or contact support.'
            );
          }

          return;
        }
      } catch (error) {
        // Log the error appropriately
        formSubmissionService.logSubmissionError(error);
      } finally {
        // Reset submission state regardless of success or failure
        setIsSubmitting(false);
      }
    } catch (error: unknown) {
      formSubmissionService.logValidationError(error);
      toast.error('An unexpected error occurred');
      setIsSubmitting(false);
    }
  };

  return {
    handleSubmit,
    handleNavigation,
    isSubmitting,
    proceedWithSubmission: () => {}, // Deprecated - kept for backward compatibility
  };
};
